import Button from "../../../../button";
import React, {useEffect, useRef, useState} from "react";
import SlideOverHeader from "../../../component/header";
import SlideOverBody from "../../../component/body";
import SlideOverFooter from "../../../component/footer";
import useSlideOver from "../../../useSlideOver";
import ApiDatabase from "server";
import {toast} from "react-toastify";
import RichTextEditor from "../../../../RichTextEditor/RichTextEditor";
import StyledInput from "../../../../styledInput/StyledInput";
import UseContext from "../../../../../hooks/useContext";
import {BanIcon, CheckIcon, SparklesIcon} from "../../../../../assets/icons";
import QualificationTasksTab from "./QualificationTasksTab";
import {buildTasksPayload, toFormTasks} from "./qualificationTasks";
import {qualificationText} from "./qualificationTexts";
import {
    AI_FIELDS,
    AI_STATUS,
    AI_SUGGEST_CLIENT_TIMEOUT_MS,
    AI_SUGGEST_POLL_INTERVAL_MS,
    AI_TITLE_MAX_LENGTH,
    AI_TITLE_MIN_LENGTH,
    buildRegenerateKeep,
    isValidAiTitle,
    listAiFields,
    mergeSuggestedTasks,
    noAiFields,
    normalizeRomeInput,
    toComparableText,
    toSuggestedValues,
} from "./qualificationAiSuggestion";
import {
    AiBadge,
    AiFailureMessage,
    AiFieldPills,
    AiGenerationSteps,
    AiPrefilledBanner,
    LowConfidenceAlert,
    SimilarQualificationsAlert,
} from "./QualificationAiParts";

const text = (key, values) => qualificationText(`slideOver.${key}`, values)
const aiText = (key, values) => qualificationText(`ai.${key}`, values)

const SECTOR_KEYS = ['sale', 'logistic', 'food', 'largeDistribution', 'administration', 'other']

const TAB_INFORMATIONS = 'informations'
const TAB_TASKS = 'tasks'

// Creation goes through a first step (title, AI or not), an optional
// generation screen, then the form; modification opens the form directly.
const STAGE_START = 'start'
const STAGE_GENERATING = 'generating'
const STAGE_FORM = 'form'

// Refusals added with the qualification tasks come as { error, errorCode };
// the older ones keep their { message }.
const API_ERROR_TEXTS = {
    INVALID_TASKS: {key: 'errorInvalidTasks', tab: TAB_TASKS},
    QUALIFICATION_NAME_IMMUTABLE: {key: 'errorNameImmutable', tab: TAB_INFORMATIONS},
    INVALID_PCS_CODE: {key: 'errorInvalidPcs', tab: TAB_INFORMATIONS},
    INVALID_ROME_CODE: {key: 'errorInvalidRome', tab: TAB_INFORMATIONS},
    INVALID_AI_SUGGESTION: {key: 'errorInvalidAiSuggestion', tab: TAB_INFORMATIONS},
}

const API_WARNING_TEXTS = {
    PCS_NOT_IN_PCS_ESE_2017: 'warningPcsNotInList',
}

const stopPollingSession = (polling) => {
    polling.session += 1
    clearTimeout(polling.pollTimer)
    clearTimeout(polling.deadlineTimer)
    polling.pollTimer = null
    polling.deadlineTimer = null
}

const sectorLabel = (sectorKey) => text(`sectors.${sectorKey}`) || sectorKey

// Module-level on purpose: StyledInput resets its options whenever `list`
// changes identity, so a list rebuilt at each render would loop.
const SECTOR_OPTIONS = [
    {value: '', label: text('sectorPlaceholder')},
    ...SECTOR_KEYS.map((sectorKey) => ({value: sectorKey, label: text(`sectors.${sectorKey}`)})),
]

// StyledInput's checkbox colour class matches no theme colour: tint and align it from the wrapper.
const CHECKBOX_WRAPPER_CLASS = '[&_input]:accent-yellow-500 [&_input]:self-start [&_input]:mt-0.5'

const FieldLabel = ({children, isAi}) => (
    <div className={'flex items-center'}>
        {children}
        {isAi && <AiBadge/>}
    </div>
)

// StyledInput only renders `hint` for textarea and select fields; text fields get it here, same style.
const WithHint = ({hint, children}) => (
    <div className={'flex flex-col gap-1'}>
        {children}
        {hint && <div className={'text-gray-500 text-xs'}>{hint}</div>}
    </div>
)

const SlideOverQualification = () => {
    const {toggleSlideOver} = useSlideOver();
    const {refreshQualifications, setRefreshQualifications, refreshQualification, setRefreshQualification, objID, setObjID} = UseContext()
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [initialDescription, setInitialDescription] = useState('')
    const [pcs, setPcs] = useState('')
    const [rome, setRome] = useState('')
    const [type, setType] = useState('')
    const [isToDisplayInMissionPreferences, setIsToDisplayInMissionPreferences] = useState(false)
    const [isActive, setIsActive] = useState(true)
    const [isLoading, setIsLoading] = useState(false)
    const [tasks, setTasks] = useState([])
    const [activeTab, setActiveTab] = useState(TAB_INFORMATIONS)
    const [submitError, setSubmitError] = useState('')
    const [stage, setStage] = useState(STAGE_START)
    const [isAiAvailable, setIsAiAvailable] = useState(false)
    const [useAi, setUseAi] = useState(false)
    const [aiTitle, setAiTitle] = useState('')
    const [aiTitleError, setAiTitleError] = useState('')
    const [generationStatus, setGenerationStatus] = useState(AI_STATUS.PENDING)
    // The classified answer, for the values the finished steps show.
    const [generationProgress, setGenerationProgress] = useState(null)
    const [aiFailed, setAiFailed] = useState(false)
    // The last `done` suggestion applied: id, confidences, alternatives, similar qualifications.
    const [suggestion, setSuggestion] = useState(null)
    // Which fields still hold the AI value as proposed.
    const [aiFields, setAiFields] = useState(noAiFields)
    const [romeLabel, setRomeLabel] = useState('')
    const [pcsLabel, setPcsLabel] = useState('')
    const token = localStorage.getItem('xsrfToken')

    // A new session invalidates every callback of the previous one: answers
    // arriving after a stop (close, failure, regeneration) are dropped.
    const pollingRef = useRef({session: 0, pollTimer: null, deadlineTimer: null})
    // What the editor first reports for the AI description; later reports that
    // differ from it are edits by the admin.
    const aiDescriptionRef = useRef({html: '', baseline: null})

    const isCreation = !objID
    const currentStage = isCreation ? stage : STAGE_FORM

    const stopPolling = () => stopPollingSession(pollingRef.current)

    useEffect(() => {
        const polling = pollingRef.current
        return () => stopPollingSession(polling)
    }, [])

    useEffect(() => {
        if (objID) return
        ApiDatabase.getQualificationAiSuggestStatus({}, (data) => {
            const isEnabled = data?.enabled === true
            setIsAiAvailable(isEnabled)
            setUseAi(isEnabled)
        }, () => {
            setIsAiAvailable(false)
            setUseAi(false)
        })
    }, [objID]);

    const markFieldEdited = (field) => {
        setAiFields((fields) => (fields[field] ? {...fields, [field]: false} : fields))
    }

    const handleSubmitError = (err) => {
        if (err.name === "CanceledError") return
        console.log(err)
        setIsLoading(false)
        const errorCode = err.response?.data?.errorCode
        const known = API_ERROR_TEXTS[errorCode]
        if (known) {
            if (errorCode === 'INVALID_AI_SUGGESTION') {
                // The next "Créer" goes without the stale suggestion.
                setSuggestion(null)
                setAiFields(noAiFields())
            }
            setSubmitError(text(known.key))
            setActiveTab(known.tab)
            return
        }
        toast.error(err.response?.data?.message || err.response?.data?.error)
    }

    const showResponseWarnings = (data) => {
        const warnings = Array.isArray(data?.warnings) ? data.warnings : []
        warnings.forEach((warning) => {
            const key = API_WARNING_TEXTS[warning?.code]
            if (key) toast.warning(text(key))
        })
    }

    const handleSubmit = () => {
        if(name && description && pcs && type) {
            setIsLoading(true)
            setSubmitError('')
            const payload = {token, name, description, pcs, rome, type, isToDisplayInMissionPreferences, tasks: buildTasksPayload(tasks)}
            if(objID) {
                ApiDatabase.updateQualification({...payload, idQualification: objID}, (data) => {
                    toast.success(text('updated'))
                    showResponseWarnings(data)
                    setIsLoading(false)
                    setRefreshQualification(data)
                    quitSlideOver()
                }, handleSubmitError)
            } else {
                const aiPayload = suggestion
                    ? {aiSuggestionId: suggestion.suggestionId, aiFields: listAiFields(aiFields)}
                    : {}
                ApiDatabase.postQualification({...payload, ...aiPayload}, (data) => {
                    toast.success(text('created'))
                    showResponseWarnings(data)
                    setIsLoading(false)
                    setRefreshQualifications(!refreshQualifications)
                    quitSlideOver()
                }, handleSubmitError)
            }
        } else {
            toast.error(text('missingFields'))
        }
    }

    const onEditorStateChange = (html) => {
        setDescription(html)
        if (!aiFields.description) return
        const aiDescription = aiDescriptionRef.current
        if (aiDescription.baseline === null && toComparableText(html) === toComparableText(aiDescription.html)) {
            aiDescription.baseline = html
            return
        }
        if (html !== aiDescription.baseline) markFieldEdited('description')
    }

    useEffect(() => {
        if(objID) {
            ApiDatabase.getQualificationByID({token, idQualification: objID}, (data) => {
                setName(data.name)
                setDescription(data.desc)
                setInitialDescription(data.desc)
                setPcs(data.codePCS)
                setRome(data.codeROME)
                setType(data.type)
                setIsToDisplayInMissionPreferences(data.displayInMissionPreferences)
                setIsActive(data.isActive)
                setTasks(toFormTasks(data.tasks))
            }, (err) => {
                if (err.name !== "CanceledError") {
                    console.log(err)
                    toast.error(err.response.data.message)
                }
            })
        }
    }, [objID]);

    const quitSlideOver = () => {
        stopPolling()
        toggleSlideOver()
        setObjID('')
    }

    const showAiFailure = () => {
        stopPolling()
        setAiFailed(true)
        setStage(STAGE_FORM)
    }

    /** Fills the fields still "AI" (all of them the first time) and the untouched AI tasks. */
    const applySuggestion = (data, replacedFields, replacesTasks) => {
        const values = toSuggestedValues(data)
        const replaces = (field) => replacedFields.includes(field)
        if (replaces('name')) setName(values.name)
        if (replaces('description')) {
            setDescription(values.description)
            setInitialDescription(values.description)
            aiDescriptionRef.current = {html: values.description, baseline: null}
        }
        if (replaces('rome')) {
            setRome(values.rome)
            setRomeLabel(data.rome?.label ?? '')
        }
        if (replaces('pcs')) {
            setPcs(values.pcs)
            setPcsLabel(data.pcs?.label ?? '')
        }
        if (replaces('sector')) setType(values.sector)
        if (replacesTasks) setTasks((current) => mergeSuggestedTasks(current, data.tasks))
        setAiFields((fields) => AI_FIELDS.reduce((next, field) => ({
            ...next,
            [field]: replaces(field) ? true : fields[field],
        }), {}))
        setSuggestion({
            suggestionId: data.suggestionId,
            rome: data.rome,
            pcs: data.pcs,
            alternatives: {
                rome: data.alternatives?.rome ?? [],
                pcs: data.alternatives?.pcs ?? [],
            },
            similarQualifications: data.similarQualifications ?? [],
        })
        setAiFailed(false)
        setActiveTab(TAB_INFORMATIONS)
        setStage(STAGE_FORM)
    }

    /** Asks the api for a suggestion, then polls it until done, failed, closed or 4 minutes. */
    const startSuggestion = (title, regenerate) => {
        stopPolling()
        const session = pollingRef.current.session
        const isCurrent = () => pollingRef.current.session === session
        const replacedFields = regenerate
            ? AI_FIELDS.filter((field) => !regenerate.keep.includes(field))
            : AI_FIELDS
        const replacesTasks = !regenerate || !regenerate.keep.includes('tasks')

        setGenerationStatus(AI_STATUS.PENDING)
        setGenerationProgress(null)
        setAiFailed(false)
        setSubmitError('')
        // The form remounts after the generation screen: the editor restarts from the current text.
        setInitialDescription(description)
        setStage(STAGE_GENERATING)

        pollingRef.current.deadlineTimer = setTimeout(() => {
            if (isCurrent()) showAiFailure()
        }, AI_SUGGEST_CLIENT_TIMEOUT_MS)

        const failIfCurrent = () => {
            if (isCurrent()) showAiFailure()
        }

        const poll = (suggestionId) => {
            pollingRef.current.pollTimer = setTimeout(() => {
                ApiDatabase.getQualificationAiSuggestion({suggestionId}, (data) => {
                    if (!isCurrent()) return
                    if (data?.status === AI_STATUS.DONE) {
                        stopPolling()
                        applySuggestion(data, replacedFields, replacesTasks)
                        return
                    }
                    if (data?.status === AI_STATUS.FAILED) {
                        showAiFailure()
                        return
                    }
                    if (data?.status === AI_STATUS.CLASSIFIED) {
                        setGenerationStatus(AI_STATUS.CLASSIFIED)
                        setGenerationProgress(data)
                    }
                    poll(suggestionId)
                }, failIfCurrent)
            }, AI_SUGGEST_POLL_INTERVAL_MS)
        }

        const payload = {title: title.trim(), ...(regenerate ? {regenerate} : {})}
        ApiDatabase.postQualificationAiSuggestion(payload, (data) => {
            if (!isCurrent()) return
            if (!data?.suggestionId) {
                showAiFailure()
                return
            }
            poll(data.suggestionId)
        }, failIfCurrent)
    }

    const handleStart = () => {
        const title = name.trim()
        if (!title) return
        if (isAiAvailable && useAi) {
            if (!isValidAiTitle(title)) {
                setAiTitleError(aiText('titleLength', {min: AI_TITLE_MIN_LENGTH, max: AI_TITLE_MAX_LENGTH}))
                return
            }
            setAiTitle(title)
            startSuggestion(title)
            return
        }
        setStage(STAGE_FORM)
    }

    // Cancelling a regeneration goes back to the form the admin already filled;
    // only the first generation has nothing to go back to.
    const cancelGeneration = () => {
        if (!suggestion) {
            quitSlideOver()
            return
        }
        stopPolling()
        setStage(STAGE_FORM)
    }

    const handleRegenerate = () => {
        if (!suggestion) return
        startSuggestion(aiTitle, {
            suggestionId: suggestion.suggestionId,
            keep: buildRegenerateKeep(aiFields, tasks),
        })
    }

    const pickAlternative = (field) => (alternative) => {
        if (field === 'rome') {
            setRome(alternative.code)
            setRomeLabel(alternative.label ?? '')
        } else {
            setPcs(alternative.code)
            setPcsLabel(alternative.label ?? '')
        }
        // An alternative offered by the AI keeps the field "AI".
        setAiFields((fields) => ({...fields, [field]: true}))
    }

    const delQualification = () => {
        if(objID) {
            ApiDatabase.delQualification({token, idQualification: objID}, (data) => {
                toast.success(text('deactivated'))
                setRefreshQualification(data)
                quitSlideOver()
            }, (err) => {
                if (err.name !== "CanceledError") {
                    console.log(err)
                    toast.error(err.response.data.message)
                }
            })
        }
    }

    const activeQualification = () => {
        if(objID) {
            ApiDatabase.activateQualification({token, idQualification: objID}, (data) => {
                toast.success(text('activated'))
                setRefreshQualification(data)
                quitSlideOver()
            }, (err) => {
                if (err.name !== "CanceledError") {
                    console.log(err)
                    toast.error(err.response.data.message)
                }
            })
        }
    }

    const tabs = [
        {id: TAB_INFORMATIONS, label: text('tabInformations')},
        {
            id: TAB_TASKS,
            label: text('tabTasks'),
            count: tasks.length,
            // The count badge alone would read "Tâches 8"; the accessible name keeps the explicit form.
            ariaLabel: text('tabTasksWithCount', {count: tasks.length}),
        },
    ]

    const isLowConfidence = (field) => aiFields[field] && suggestion?.[field]?.confidence === 'low'

    const headerSubtitle = () => {
        if (isCreation) return text('subtitle')
        return activeTab === TAB_TASKS ? text('editTasksSubtitle') : text('editSubtitle')
    }

    // A disabled fieldset disables the box without StyledInput's grey label background.
    const renderMissionPreferences = (isDisabled = false) => (
        <fieldset
            disabled={isDisabled}
            className={`min-w-0 rounded-lg border border-gray-200 p-4 select-none ${CHECKBOX_WRAPPER_CLASS} ${isDisabled ? 'opacity-50' : ''}`}
        >
            <StyledInput
                type="checkbox"
                checked={isToDisplayInMissionPreferences}
                onChange={() => setIsToDisplayInMissionPreferences(!isToDisplayInMissionPreferences)}
                label={
                    <div className={'flex flex-col gap-0.5'}>
                        <div className={'text-gray-900'}>{text('missionPreferences')}</div>
                        <div className={'text-gray-500'}>{text('missionPreferencesHelp')}</div>
                    </div>
                }
            />
        </fieldset>
    )

    const renderStartStage = () => (
        <div className={'flex flex-col gap-4'}>
            <WithHint hint={aiTitleError ? '' : text('nameWillLock')}>
                <StyledInput
                    type="text"
                    label={text('name')}
                    value={name}
                    error={aiTitleError}
                    onChange={e => {
                        setName(e.target.value)
                        setAiTitleError('')
                    }}
                />
            </WithHint>
            {isAiAvailable && (
                <div className={`flex flex-col gap-3 rounded-lg bg-yellow-50 p-4 select-none ${CHECKBOX_WRAPPER_CLASS}`}>
                    <StyledInput
                        type="checkbox"
                        checked={useAi}
                        onChange={() => {
                            setUseAi(!useAi)
                            setAiTitleError('')
                        }}
                        label={<span className={'font-medium text-gray-900'}>{aiText('useAi')}</span>}
                    />
                    {useAi && (
                        <div className={'flex flex-col gap-2'}>
                            <span className={'ft-sm text-gray-700'}>{aiText('useAiHelp')}</span>
                            <AiFieldPills/>
                        </div>
                    )}
                </div>
            )}
            {renderMissionPreferences()}
        </div>
    )

    const renderGeneratingStage = () => (
        <div className={'flex flex-col gap-4'}>
            <StyledInput
                type="text"
                label={text('name')}
                value={aiTitle}
                isDisabled
            />
            <AiGenerationSteps
                status={generationStatus}
                progress={generationProgress}
                sectorLabel={sectorLabel}
            />
            {renderMissionPreferences(true)}
        </div>
    )

    const renderTabs = () => (
        <div role="tablist" className={'flex gap-2 mb-4'}>
            {tabs.map((tab) => {
                const isSelected = activeTab === tab.id
                return (
                    <Button
                        key={tab.id}
                        color={'TABLE'}
                        size={'SM'}
                        role="tab"
                        id={`qualification-tab-${tab.id}`}
                        aria-selected={isSelected}
                        aria-controls={`qualification-panel-${tab.id}`}
                        aria-label={tab.ariaLabel}
                        className={isSelected ? 'bg-gray-100' : ''}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        <span className={isSelected ? 'font-medium text-gray-900' : 'text-gray-500'}>
                            {tab.label}
                        </span>
                        {tab.count !== undefined && (
                            <span className={'rounded bg-yellow-100 px-1.5 text-xs font-medium text-yellow-700'}>
                                {tab.count}
                            </span>
                        )}
                    </Button>
                )
            })}
        </div>
    )

    const renderFormStage = () => (
        <>
            {isCreation && aiFailed && <AiFailureMessage/>}
            {renderTabs()}
            {isCreation && suggestion && (
                <AiPrefilledBanner onRegenerate={handleRegenerate} disabled={isLoading}/>
            )}
            {submitError && (
                <div role="alert" className={'mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700'}>
                    {submitError}
                </div>
            )}
            {/* Both panels stay mounted: the description editor would lose its content on a remount.
                The class must drop `flex` when hidden: `.flex` beats preflight's `[hidden]` rule (same specificity, declared later). */}
            <div
                role="tabpanel"
                id={`qualification-panel-${TAB_INFORMATIONS}`}
                aria-labelledby={`qualification-tab-${TAB_INFORMATIONS}`}
                hidden={activeTab !== TAB_INFORMATIONS}
                className={activeTab === TAB_INFORMATIONS ? 'flex flex-col gap-4' : 'hidden'}
            >
                {isCreation && suggestion?.similarQualifications?.length > 0 && (
                    <SimilarQualificationsAlert
                        qualifications={suggestion.similarQualifications}
                        sectorLabel={sectorLabel}
                    />
                )}
                <WithHint hint={objID ? text('nameLocked') : text('nameWillLock')}>
                    <StyledInput
                        type="text"
                        label={<FieldLabel isAi={aiFields.name}>{text('name')}</FieldLabel>}
                        value={name}
                        isDisabled={Boolean(objID)}
                        onChange={e => {
                            setName(e.target.value)
                            markFieldEdited('name')
                        }}
                    />
                </WithHint>
                <WithHint hint={text('descriptionHint')}>
                    <label className={'flex flex-col gap-1 [&_.rich-text-editor]:rounded-md'}>
                        <div className={'ft-sm text-gray-500'}>
                            <FieldLabel isAi={aiFields.description}>{text('description')}</FieldLabel>
                        </div>
                        <RichTextEditor initialContent={initialDescription} onEditorStateChange={onEditorStateChange}/>
                    </label>
                </WithHint>
                <div className={'grid grid-cols-2 gap-3'}>
                    <WithHint hint={aiFields.rome ? romeLabel : ''}>
                        <StyledInput
                            type="text"
                            label={<FieldLabel isAi={aiFields.rome}>{text('rome')}</FieldLabel>}
                            isOptional
                            value={rome}
                            onChange={e => {
                                setRome(normalizeRomeInput(e.target.value))
                                setRomeLabel('')
                                markFieldEdited('rome')
                            }}
                        />
                    </WithHint>
                    <WithHint hint={aiFields.pcs ? pcsLabel : ''}>
                        <StyledInput
                            type="text"
                            label={<FieldLabel isAi={aiFields.pcs}>{text('pcs')}</FieldLabel>}
                            value={pcs}
                            onChange={e => {
                                setPcs(e.target.value)
                                setPcsLabel('')
                                markFieldEdited('pcs')
                            }}
                        />
                    </WithHint>
                </div>
                {isCreation && isLowConfidence('rome') && (
                    <LowConfidenceAlert
                        field="rome"
                        alternatives={suggestion.alternatives.rome}
                        onPick={pickAlternative('rome')}
                    />
                )}
                {isCreation && isLowConfidence('pcs') && (
                    <LowConfidenceAlert
                        field="pcs"
                        alternatives={suggestion.alternatives.pcs}
                        onPick={pickAlternative('pcs')}
                    />
                )}
                <StyledInput
                    type="select"
                    label={<FieldLabel isAi={aiFields.sector}>{text('sector')}</FieldLabel>}
                    list={SECTOR_OPTIONS}
                    value={type}
                    onChange={(e) => {
                        setType(e.target.value)
                        markFieldEdited('sector')
                    }}
                />
                {renderMissionPreferences()}
            </div>
            <div
                role="tabpanel"
                id={`qualification-panel-${TAB_TASKS}`}
                aria-labelledby={`qualification-tab-${TAB_TASKS}`}
                hidden={activeTab !== TAB_TASKS}
            >
                <QualificationTasksTab tasks={tasks} setTasks={setTasks}/>
            </div>
        </>
    )

    const renderFooter = () => {
        if (isLoading) {
            return (
                <div className={'flex justify-between w-full'}>
                    <div className={'flex justify-end gap-2 w-full'}>
                        <Button size={'LG'} color={'DISABLED'}>{text('cancel')}</Button>
                        <Button size={'LG'} color={'DISABLED'}>{text('confirming')}</Button>
                    </div>
                </div>
            )
        }
        if (currentStage === STAGE_START) {
            const willGenerate = isAiAvailable && useAi
            const startLabel = willGenerate ? aiText('generate') : aiText('continue')
            return (
                <div className={'flex justify-end gap-2 w-full'}>
                    <Button size={'LG'} color={'SECONDARY'} onClick={quitSlideOver}>{text('cancel')}</Button>
                    <Button size={'LG'} color={name.trim() ? 'PRIMARY' : 'DISABLED'} disabled={!name.trim()} onClick={handleStart}>
                        {willGenerate && <SparklesIcon wh={20} color={name.trim() ? '#FFFFFF' : '#9CA3AF'}/>}
                        {startLabel}
                    </Button>
                </div>
            )
        }
        if (currentStage === STAGE_GENERATING) {
            return (
                <div className={'flex justify-end gap-2 w-full'}>
                    <Button size={'LG'} color={'SECONDARY'} onClick={cancelGeneration}>{text('cancel')}</Button>
                    {/* PRIMARYICO has no hover state, so the pale orange stays put while disabled. */}
                    <Button size={'LG'} color={'PRIMARYICO'} className={'opacity-50 cursor-not-allowed'} disabled>
                        <SparklesIcon wh={20} color={'#FFFFFF'}/>
                        {aiText('generatingButton')}
                    </Button>
                </div>
            )
        }
        return (
            <div className={'flex justify-between w-full'}>
                {(objID && isActive) && (
                    <div className={'flex'}>
                        <Button size={'LG'} color={'SECONDARY'} onClick={() => delQualification()}>
                            <div className={'flex gap-2'}>
                                <div className={'my-auto'}>
                                    <BanIcon wh={20} color={'#111827'}/>
                                </div>
                                <div className={'my-auto'}>
                                    {text('deactivate')}
                                </div>
                            </div>
                        </Button>
                    </div>
                )}
                {(objID && !isActive) && (
                    <div className={'flex'}>
                        <Button size={'LG'} color={'DEFAULT'} onClick={() => activeQualification()}>
                            <div className={'flex gap-2'}>
                                <div className={'my-auto'}>
                                    <CheckIcon w={24} h={12} color={'#111827'}/>
                                </div>
                                <div className={'my-auto'}>
                                    {text('activate')}
                                </div>
                            </div>
                        </Button>
                    </div>
                )}
                <div className={'flex justify-end gap-2 w-full'}>
                    <Button size={'LG'} color={'SECONDARY'} onClick={quitSlideOver}>{text('cancel')}</Button>
                    <Button size={'LG'} color={'PRIMARY'} onClick={handleSubmit}>{objID ? text('confirm') : text('create')}</Button>
                </div>
            </div>
        )
    }

    return (
        <>
            <SlideOverHeader hide={quitSlideOver} hideShow={true}>
                {objID ? text('editTitle') : text('createTitle')}
                <div className={'text-gray-500 ft-sm font-normal mt-1'}>
                    {headerSubtitle()}
                </div>
            </SlideOverHeader>
            <SlideOverBody>
                {currentStage === STAGE_START && renderStartStage()}
                {currentStage === STAGE_GENERATING && renderGeneratingStage()}
                {currentStage === STAGE_FORM && renderFormStage()}
            </SlideOverBody>
            <SlideOverFooter>
                {renderFooter()}
            </SlideOverFooter>
        </>
    )
}

export default SlideOverQualification
